import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { getHelmetData } from 'utils/fnHelmet';
import SVGInline from 'components/SVGInline/SVGInline';
import translate from 'utils/fnTranslate';
import icons from 'style';
import { useApp } from 'providers/useApp/AppContext';
import { useGlobalNetworkError, useGlobalNoInternet } from 'hooks/withNetworkCheck/withNetworkCheck';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import GSApi from 'api/Api';
import KeycloakApi from 'api/KeycloakApi';
import { TrackingElementPropTypes } from 'types/Tracking';
import { FetchParamById } from 'types/ApiTypes';
import { useNavigation } from 'hooks/useNavigation';
import { StreamSessionsDetailsResponse } from 'types/Player';
import { AlertDialog } from '../../../AlertDialog/AlertDialog';
import { ErrorMessage } from '../PageSettingsCommon.css';
import { PageWrapper, Title, SelectHint, DeviceList, SignOutButton, Icon } from './PageTakeover.css';
import OptionFocusable from '../OptionFocusable';

export enum DeviceType {
    mobile = 'mobile',
    tablet = 'tablet',
    computer = 'computer',
    browser = 'browser',
    tv = 'tv',
    stb = 'stb',
    ios = 'ios',
    astv = 'astv',
    android = 'android',
    smartTvApp = 'smart-tv-app',
}

type DevicePropType = TrackingElementPropTypes & {
    icon: any;
    name: string;
    metaData: StreamSessionsDetailsResponse;
    description?: string;
    onClick: () => void;
    index: number;
};

const Alert: React.FC<{ uuid: string; onSuccess: () => void; onClose: () => void }> = ({ uuid, onSuccess, onClose }) => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);

    const {
        error,
        fetcher: endStreamSession,
        responseCode,
    } = useDataFetcher<any, FetchParamById>((params) => GSApi.endStreamSession(params.id), onNoInternet, onNetworkError);

    useEffect(() => {
        if (responseCode?.toString().startsWith('2')) {
            onSuccess();
        }
    }, [responseCode, onSuccess]);

    useEffect(() => {
        if ((responseCode && !responseCode?.toString().startsWith('2')) || error) {
            setShowErrorAlert(true);
        }
    }, [responseCode, error]);

    return (
        <AlertDialog
            title={translate(showErrorAlert ? 'TAKE_OVER_FAILED_SCREEN_TITLE' : 'CONFIRMATION_SCREEN_TITLE')}
            bodyText={translate(showErrorAlert ? 'TAKE_OVER_FAILED_SCREEN_HINT' : 'CONFIRMATION_SCREEN_HINT')}
            buttons={[
                {
                    text: translate('CANCEL_BUTTON'),
                    onClick: onClose,
                },
                {
                    text: translate(showErrorAlert ? 'TRY_AGAIN_BUTTON' : 'TAKE_OVER_BUTTON'),
                    onClick: () => {
                        endStreamSession({ id: uuid });
                    },
                },
            ]}
        />
    );
};

const DeviceItem: React.FC<DevicePropType> = ({ icon, name, metaData, onClick }) => {
    const detailedTitle = metaData?.client_id === DeviceType.browser ? metaData?.browser : metaData?.device_type;
    const deviceTitle = metaData ? `${detailedTitle}, ${metaData?.os}` : name;

    return (
        <OptionFocusable
            title={deviceTitle}
            // subtitle={description}
            icon={icon}
            role="button"
            onClick={onClick}
            actionComponent={
                <SignOutButton aria-hidden>
                    <Icon>
                        <SVGInline svg={icons.icoTrash} />
                    </Icon>
                </SignOutButton>
            }
            ariaHint={translate('ARIA_HINT_DEVICE_SIGNOUT')}
        />
    );
};

export const PageDeviceManager: FC<{ limitReached?: boolean; onSessionDeleted?: () => void }> = ({ limitReached, onSessionDeleted }) => {
    const { setPageTitle } = useApp();
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { onBackPressed } = useNavigation();
    const [streamTakeover, setStreamTakeover] = useState<string | null>(null);
    const [streamLimitReached, setStreamLimitReached] = useState<boolean>(false);

    const {
        response: sessionsResponse,
        error,
        fetcher: getActiveStreamSessions,
    } = useDataFetcher<any, any>(() => GSApi.activeStreamSessions(), onNoInternet, onNetworkError);
    const {
        response: detailsResponse,
        error: detailsError,
        fetcher: getSessionDetails,
    } = useDataFetcher<any, any>(() => KeycloakApi.getDeviceSessions(), onNoInternet, onNetworkError);

    const getDeviceIcon = (deviceType: string, deviceName?: string) => {
        switch (deviceType) {
            case DeviceType.ios:
                if (deviceName?.toLowerCase().includes('ipad')) return icons.icoDeviceTablet;
                return icons.icoDevicePhone;
            case DeviceType.android:
            case DeviceType.mobile:
                return icons.icoDevicePhone;
            case DeviceType.tablet:
                return icons.icoDeviceTablet;
            case DeviceType.computer:
            case DeviceType.browser:
                return icons.icoDeviceDesktop;
            case DeviceType.stb:
                return icons.icoDeviceStb;
            case DeviceType.smartTvApp:
            case DeviceType.astv:
            case DeviceType.tv:
                return icons.icoDeviceTv;
            default:
                return null;
        }
    };

    const getSessionMetaData = (session) => {
        if (detailsError) return null;

        return detailsResponse?.find((obj) => obj.device_uuid === session.device_id);
    };

    const deviceItems: ReactNode[] = sessionsResponse?.sessions
        ? sessionsResponse.sessions.map((session) => (
              <DeviceItem
                  index={session.id}
                  key={`device-${session.id}`}
                  icon={getDeviceIcon(session.app_code, session.app_name)}
                  name={`${session.app_name}, ${session.device_name}`}
                  // description={`${translate('SETTINGS_DEVICE_LAST_USED')}: ${format(Date.parse(device.last_seen), 'dd.MM.yyyy')}`}
                  onClick={() => setStreamTakeover(session.id)}
                  metaData={getSessionMetaData(session)}
              />
          ))
        : null;

    const retryTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        if (sessionsResponse?.sessions?.length) {
            getSessionDetails(null);
        } else if (sessionsResponse && !sessionsResponse.sessions?.length && !retryTimeoutRef.current) {
            // Sometimes the sessionsResponse.sessions is empty array (somehow), this is re-try
            retryTimeoutRef.current = window.setTimeout(() => {
                getActiveStreamSessions(null);
            }, 2000);
        }
    }, [sessionsResponse]);

    useEffect(() => {
        getActiveStreamSessions(null);
        setPageTitle(translate('MANAGE_STREAMS_SCREEN_TITLE'));

        if (limitReached) {
            setStreamLimitReached(true);
        }

        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, []);

    return (
        <HelmetWrapper {...getHelmetData(translate('MANAGE_STREAMS_SCREEN_TITLE'))}>
            <PageWrapper>
                <Title>{translate('MANAGE_STREAMS_SCREEN_TITLE')}</Title>
                {error ? (
                    <ErrorMessage>{translate('SETTINGS_ERROR_HINT')}</ErrorMessage>
                ) : (
                    <>
                        <SelectHint>{translate('MANAGE_STREAMS_SCREEN_HINT_MOBILE')}</SelectHint>
                        <SelectHint dangerouslySetInnerHTML={{ __html: translate('MANAGE_STREAMS_SCREEN_UPSELL_HINT') }} />
                        {deviceItems?.length > 0 && <DeviceList>{deviceItems}</DeviceList>}
                    </>
                )}
            </PageWrapper>
            {streamTakeover && (
                <Alert
                    uuid={streamTakeover}
                    onSuccess={() => {
                        setStreamTakeover(null);
                        if (onSessionDeleted) {
                            onSessionDeleted();
                        } else {
                            getActiveStreamSessions(null);
                            onBackPressed();
                        }
                    }}
                    onClose={() => setStreamTakeover(null)}
                />
            )}
            {streamLimitReached && (
                <AlertDialog
                    transparent
                    title={translate('PLAY_SLOTS_ERROR_SCREEN_TITLE', {
                        stream_count: sessionsResponse?.count,
                        max_stream_count: sessionsResponse?.limit,
                    })}
                    bodyText={translate('PLAY_SLOTS_ERROR_SCREEN_HINT')}
                    buttons={[
                        {
                            text: translate('CANCEL_BUTTON'),
                            onClick: () => {
                                setStreamLimitReached(false);
                                onBackPressed();
                            },
                        },
                        {
                            text: translate('MANAGE_STREAMS_BUTTON'),
                            onClick: () => setStreamLimitReached(false),
                        },
                    ]}
                />
            )}
        </HelmetWrapper>
    );
};

export default PageDeviceManager;
